import React, { useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../helpers/datetime";
import { useAppDispatch, useAppSelector } from "../app/appHooks";
import { ReducerStates } from "../../helpers/constants";
import { ISSOConfig, settingsServices } from "../../services/settings.services";
import { getClientID } from "../login/loginSlice";
import { ConfirmDialog, DialogTypes } from "../common/ConfirmDialog";
import {
  createOrganizationSSOConfiguration,
  deleteOrganizationSSOConfiguration,
} from "./settingsThunks";

const ENTRA_SETUP_GUIDE_URL =
  "https://docs.google.com/document/d/e/2PACX-1vQVWez25ABZ56mt-A6hXglm6nPjxOEvs_j3-FW23M2AqxnzJe8APuaZH22E_tO4BuBBW8ON9K1KS5jn/pub";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(5.5, 6),
      width: "680px",
    },
    sectionTitle: {
      marginTop: 0,
      marginBottom: theme.spacing(4),
      textAlign: "center",
    },
    startSetup: {
      color: theme.palette.primary.main,
      cursor: "pointer",
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: theme.typography.subtitle1.fontSize,
      fontWeight: 600,
      outlineColor: theme.palette.primary.main,
      textAlign: "center",
    },
    configGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      columnGap: theme.spacing(4),
      rowGap: theme.spacing(1),
    },
    configSummary: {
      alignItems: "flex-start",
      display: "grid",
      gridTemplateColumns: "1fr auto",
      columnGap: theme.spacing(2),
    },
    detailsInnerWidth: {
      marginLeft: "auto",
      marginRight: "auto",
      maxWidth: "560px",
      width: "100%",
    },
    iconActions: {
      alignItems: "center",
      display: "flex",
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(-1),
    },
    iconButton: {
      padding: theme.spacing(0.5),
    },
    viewIcon: {
      color: theme.palette.dark.main,
      fontSize: "1.25rem",
    },
    deleteIcon: {
      color: theme.palette.error.main,
      fontSize: "1.25rem",
    },
    columnLabel: {
      color: theme.palette.paused.main,
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 600,
    },
    columnValue: {
      overflowWrap: "anywhere",
    },
    actions: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(5),
    },
    createButton: {
      color: theme.palette.light.light,
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 600,
      lineHeight: 1.2,
      minHeight: 0,
      paddingBottom: theme.spacing(0.75),
      paddingTop: theme.spacing(0.75),
    },
    createButtonLabel: {
      fontSize: "0.68rem",
      lineHeight: 1.2,
    },
    detailsBox: {
      border: "1px solid rgba(0, 0, 0, 0.28)",
      borderRadius: "4px",
      boxShadow: "0 2px 5px rgba(0, 0, 0, 0.25)",
      padding: theme.spacing(6, 7),
      position: "relative",
    },
    closeDetailsButton: {
      color: theme.palette.grey[700],
      padding: theme.spacing(0.5),
      position: "absolute",
      right: theme.spacing(2),
      top: theme.spacing(2),
    },
    detailRow: {
      alignItems: "baseline",
      display: "grid",
      gridTemplateColumns: "110px 1fr",
      columnGap: theme.spacing(2),
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: theme.spacing(3),
      maxWidth: "560px",
      width: "100%",
    },
    detailLabel: {
      color: theme.palette.dark.main,
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: theme.typography.caption.fontSize,
      textAlign: "right",
      textTransform: "uppercase",
    },
    detailValue: {
      minWidth: 0,
      overflowWrap: "anywhere",
    },
    detailContent: {
      maxWidth: "100%",
      minWidth: 0,
    },
    tokenValue: {
      alignItems: "center",
      display: "flex",
      gap: theme.spacing(1),
      minWidth: 0,
      width: "100%",
    },
    tokenText: {
      flex: "1 1 auto",
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    copyButton: {
      color: theme.palette.primary.main,
      flexShrink: 0,
      padding: theme.spacing(0.25),
    },
    copyIcon: {
      fontSize: "1.25rem",
    },
    tokenWarning: {
      color: theme.palette.error.main,
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1.1,
      marginTop: theme.spacing(1),
      maxWidth: "390px",
    },
    tokenError: {
      color: theme.palette.error.main,
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1.2,
      marginTop: theme.spacing(1),
    },
    detailsSectionTitle: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: theme.typography.h6.fontSize,
      fontWeight: 600,
      marginBottom: 0,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: theme.spacing(6),
      maxWidth: "560px",
      width: "100%",
    },
    linkButton: {
      color: theme.palette.contactBlue.main,
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 600,
      minHeight: 0,
      padding: 0,
      textTransform: "none",
    },
    linkButtonLabel: {
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1.2,
    },
    deleteButton: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.light.light,
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 600,
      minHeight: 0,
      paddingBottom: theme.spacing(0.6),
      paddingTop: theme.spacing(0.6),
      "&:hover": {
        backgroundColor: theme.palette.error.dark,
      },
    },
    deleteButtonLabel: {
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1.2,
    },
    detailsDeleteAction: {
      display: "flex",
      justifyContent: "center",
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: theme.spacing(8),
      maxWidth: "560px",
      width: "100%",
    },
    wizardPaper: {
      borderRadius: "6px",
      boxSizing: "border-box",
      height: "684px",
      maxHeight: "calc(100vh - 64px)",
      maxWidth: "890px",
      width: "890px",
    },
    wizardContent: {
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflowY: "auto",
      padding: theme.spacing(3, 7, 4, 7),
      position: "relative",
    },
    wizardCloseButton: {
      color: theme.palette.grey[700],
      padding: theme.spacing(0.5),
      position: "absolute",
      right: theme.spacing(3),
      top: theme.spacing(3),
    },
    wizardTitle: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: theme.typography.h5.fontSize,
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
      textAlign: "center",
    },
    wizardStepTitle: {
      fontSize: theme.typography.subtitle1.fontSize,
      marginBottom: theme.spacing(2),
      textAlign: "center",
    },
    wizardSectionTitle: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 600,
      marginBottom: theme.spacing(1.5),
    },
    wizardRow: {
      alignItems: "center",
      display: "grid",
      gridTemplateColumns: "30px 1fr",
      columnGap: theme.spacing(1.5),
      marginTop: theme.spacing(1.5),
    },
    wizardCheckbox: {
      color: theme.palette.primary.main,
      padding: 0,
      "&.Mui-checked": {
        color: theme.palette.primary.main,
      },
      "&.Mui-disabled.Mui-checked": {
        color: theme.palette.primary.main,
      },
    },
    wizardInstruction: {
      fontSize: theme.typography.body2.fontSize,
      lineHeight: 1.25,
    },
    wizardLink: {
      color: theme.palette.primary.main,
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 600,
    },
    wizardForm: {
      marginTop: theme.spacing(1.5),
    },
    wizardFormRow: {
      alignItems: "center",
      display: "grid",
      gridTemplateColumns: "240px 380px",
      columnGap: theme.spacing(2),
      justifyContent: "center",
      marginTop: theme.spacing(2),
    },
    wizardFormLabel: {
      textAlign: "right",
    },
    wizardInput: {
      "& .MuiInputBase-root": {
        fontSize: theme.typography.body2.fontSize,
      },
      "& .MuiOutlinedInput-input": {
        paddingBottom: theme.spacing(1.5),
        paddingTop: theme.spacing(1.5),
      },
    },
    wizardSubmitRow: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(2),
    },
    wizardError: {
      color: theme.palette.error.main,
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1.2,
      marginTop: theme.spacing(1.5),
      textAlign: "center",
    },
    wizardSuccess: {
      color: theme.palette.primary.main,
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1.2,
      marginTop: theme.spacing(1),
      textAlign: "center",
    },
    wizardCompletionMessage: {
      fontSize: theme.typography.body2.fontSize,
      lineHeight: 1.25,
      margin: theme.spacing(10, "auto", 0, "auto"),
      maxWidth: "460px",
    },
    wizardCompletionParagraph: {
      marginBottom: theme.spacing(2),
    },
    wizardReadonlyField: {
      "& .MuiInputBase-root": {
        fontSize: theme.typography.body2.fontSize,
      },
      "& .MuiInputBase-input": {
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    wizardTokenField: {
      alignItems: "center",
      border: "1px solid rgba(0, 0, 0, 0.23)",
      boxSizing: "border-box",
      display: "flex",
      height: "50px",
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(1),
      width: "380px",
    },
    wizardTokenText: {
      flex: "1 1 auto",
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    wizardPrimaryButton: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.light.light,
      minWidth: "112px",
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    wizardBottomStep: {
      marginTop: theme.spacing(3),
    },
    wizardNextRow: {
      display: "flex",
      justifyContent: "center",
      marginTop: "auto",
      paddingTop: theme.spacing(2.5),
    },
    wizardActionButton: {
      height: "32px",
      minWidth: "112px",
    },
  })
);

const getApplicationID = (config: ISSOConfig) => config.sp_entity_id ?? "";

const getCreated = (config: ISSOConfig) => config.time_created ?? "";

const getTruncatedToken = (token: string) => {
  if (token.length <= 42) return token;
  return `${token.slice(0, 42)}...`;
};

export function SSOConfiguration() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [entityID, setEntityID] = useState("");
  const [metadataURL, setMetadataURL] = useState("");
  const [setupPending, setSetupPending] = useState(false);
  const [setupError, setSetupError] = useState("");
  const [setupSubmitted, setSetupSubmitted] = useState(false);
  const [wizardTenantURL, setWizardTenantURL] = useState("");
  const [sectionOneChecks, setSectionOneChecks] = useState({
    createApp: false,
    completeSAML: false,
    submitMetadata: false,
    validateSSO: false,
  });
  const [sectionTwoChecks, setSectionTwoChecks] = useState({
    guide: false,
    credentials: false,
    testConnection: false,
    createProvisioning: false,
    startProvisioning: false,
  });
  const [sectionThreeChecks, setSectionThreeChecks] = useState({
    logo: false,
  });
  const [generatedToken, setGeneratedToken] = useState("");
  const [generateTokenPending, setGenerateTokenPending] = useState(false);
  const [generateTokenError, setGenerateTokenError] = useState("");
  const [deleteConfigurationError, setDeleteConfigurationError] = useState("");

  const organizationSettingsState = useAppSelector(
    (state) => state.settingsState.organizationSettingsState
  );
  const selectedOrganizationID = useAppSelector(
    (state) => state.headerState.selectedOrganization
  );
  const config = useAppSelector(
    (state) => state.settingsState.organizationSettings?.sso_config
  );

  const applicationID = config ? getApplicationID(config) : "";
  const created = config ? getCreated(config) : "";
  const idpReplyURL = config?.acs_url ?? "";
  const tenantURL = config?.tenant_url ?? "";
  const handleStartSetup = () => {
    setWizardStep(1);
    setEntityID("");
    setMetadataURL("");
    setSetupError("");
    setSetupSubmitted(false);
    setWizardTenantURL("");
    setGeneratedToken("");
    setSectionOneChecks({
      createApp: false,
      completeSAML: false,
      submitMetadata: false,
      validateSSO: false,
    });
    setSectionTwoChecks({
      guide: false,
      credentials: false,
      testConnection: false,
      createProvisioning: false,
      startProvisioning: false,
    });
    setSectionThreeChecks({
      logo: false,
    });
    setShowWizard(true);
  };
  const handleCreateNewConfiguration = () => {
    if (selectedOrganizationID === undefined || config === null) {
      handleStartSetup();
      return;
    }

    dispatch(deleteOrganizationSSOConfiguration(selectedOrganizationID)).then(
      (action) => {
        if (deleteOrganizationSSOConfiguration.fulfilled.match(action)) {
          setGeneratedToken("");
          setGenerateTokenError("");
          setDeleteConfigurationError("");
          setShowDetails(false);
          handleStartSetup();
        } else {
          setDeleteConfigurationError(t("something_happened"));
        }
      }
    );
  };
  const handleGenerateToken = async () => {
    if (selectedOrganizationID === undefined) return;

    setGenerateTokenPending(true);
    setGenerateTokenError("");
    try {
      const response = await settingsServices.regenerateSCIMToken(
        selectedOrganizationID
      );
      setGeneratedToken(response.scim_token ?? "");
    } catch {
      setGenerateTokenError(t("something_happened"));
    } finally {
      setGenerateTokenPending(false);
    }
  };
  const handleCopyToken = () => {
    if (generatedToken === "") return;
    navigator.clipboard.writeText(generatedToken);
  };
  const handleDeleteConfiguration = () => {
    if (selectedOrganizationID === undefined) return;

    setDeleteConfigurationError("");
    dispatch(deleteOrganizationSSOConfiguration(selectedOrganizationID)).then(
      (action) => {
        if (deleteOrganizationSSOConfiguration.fulfilled.match(action)) {
          setGeneratedToken("");
          setGenerateTokenError("");
          setShowDetails(false);
        } else {
          setDeleteConfigurationError(t("something_happened"));
        }
      }
    );
  };
  const handleCloseDetails = () => {
    setGeneratedToken("");
    setGenerateTokenError("");
    setDeleteConfigurationError("");
    setShowDetails(false);
  };
  const handleCloseWizard = () => {
    setShowWizard(false);
  };
  const handleSectionOneCheck = (key: keyof typeof sectionOneChecks) => {
    if (key === "submitMetadata") return;

    setSectionOneChecks((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };
  const handleSectionTwoCheck = (key: keyof typeof sectionTwoChecks) => {
    setSectionTwoChecks((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };
  const handleSectionThreeCheck = (key: keyof typeof sectionThreeChecks) => {
    setSectionThreeChecks((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };
  const handleFinishWizard = () => {
    setShowWizard(false);
    setShowDetails(true);
  };
  const canContinueToStepTwo =
    sectionOneChecks.createApp &&
    sectionOneChecks.completeSAML &&
    setupSubmitted &&
    sectionOneChecks.validateSSO;
  const canSubmitSetup =
    entityID.trim() !== "" &&
    metadataURL.trim() !== "" &&
    !setupPending &&
    !setupSubmitted;
  const canContinuePastStepTwo = Object.values(sectionTwoChecks).every(Boolean);
  const canFinishWizard = Object.values(sectionThreeChecks).every(Boolean);
  const handleSubmitSetup = async () => {
    if (selectedOrganizationID === undefined || !canSubmitSetup) return;

    setSetupPending(true);
    setSetupError("");
    try {
      const action = await dispatch(
        createOrganizationSSOConfiguration({
          organization_id: selectedOrganizationID,
          provider: "entra",
          sp_entity_id: entityID.trim(),
          idp_metadata_url: metadataURL.trim(),
          default_client_id: getClientID(),
          is_enabled: true,
        })
      );

      if (createOrganizationSSOConfiguration.fulfilled.match(action)) {
        setGeneratedToken(action.payload.scim_token ?? "");
        setWizardTenantURL(action.payload.tenant_url ?? "");
        setSetupSubmitted(true);
        setSectionOneChecks((current) => ({
          ...current,
          submitMetadata: true,
        }));
      } else {
        setSetupError(t("something_happened"));
      }
    } catch {
      setSetupError(t("invalid_saml_metadata"));
    } finally {
      setSetupPending(false);
    }
  };

  if (organizationSettingsState === ReducerStates.PENDING) {
    return (
      <Paper className={classes.container} elevation={0} square>
        <h2 className={classes.sectionTitle}>{t("sso_configuration")}</h2>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (organizationSettingsState === ReducerStates.FAILED) {
    return (
      <Paper className={classes.container} elevation={0} square>
        <h2 className={classes.sectionTitle}>{t("sso_configuration")}</h2>
        <Typography align="center" color="error">
          {t("something_happened")}
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper className={classes.container} elevation={0} square>
        <h2 className={classes.sectionTitle}>{t("sso_configuration")}</h2>
        {!config ? (
          <div
            className={classes.startSetup}
            onClick={handleStartSetup}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                handleStartSetup();
              }
            }}
            role="button"
            tabIndex={0}
          >
            {t("start_entra_configuration_setup")}
          </div>
        ) : (
          <>
            <div className={showDetails ? classes.detailsBox : undefined}>
              {showDetails && (
                <Tooltip title={t("close") as string}>
                  <IconButton
                    aria-label={t("close")}
                    className={classes.closeDetailsButton}
                    onClick={handleCloseDetails}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              )}
              <div
                className={
                  showDetails
                    ? `${classes.configSummary} ${classes.detailsInnerWidth}`
                    : classes.configSummary
                }
              >
                <div className={classes.configGrid}>
                  <div className={classes.columnLabel}>
                    {t("application_id")}
                  </div>
                  <div className={classes.columnLabel}>{t("created")}</div>
                  <div className={classes.columnValue}>
                    {applicationID || "-"}
                  </div>
                  <div className={classes.columnValue}>
                    {created ? formatDate(created, true) : "-"}
                  </div>
                </div>
                {!showDetails && (
                  <div className={classes.iconActions}>
                    <Tooltip title={t("view") as string}>
                      <IconButton
                        aria-label={t("view")}
                        className={classes.iconButton}
                        onClick={() => setShowDetails(true)}
                      >
                        <VisibilityIcon className={classes.viewIcon} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("delete_configuration") as string}>
                      <IconButton
                        aria-label={t("delete_configuration")}
                        className={classes.iconButton}
                        onClick={() => setConfirmDelete(true)}
                      >
                        <CloseIcon className={classes.deleteIcon} />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}
              </div>

              {showDetails && (
                <>
                  <div className={classes.detailRow}>
                    <div className={classes.detailLabel}>
                      {t("idp_reply_url")}
                    </div>
                    <div className={classes.detailValue}>
                      {idpReplyURL || "-"}
                    </div>
                  </div>

                  <h3 className={classes.detailsSectionTitle}>
                    {t("scim_provisioning")}
                  </h3>
                  <div className={classes.detailRow}>
                    <div className={classes.detailLabel}>{t("tenant_url")}</div>
                    <div className={classes.detailValue}>
                      {tenantURL || "-"}
                    </div>
                  </div>
                  <div className={classes.detailRow}>
                    <div className={classes.detailLabel}>
                      {t("secret_token")}
                    </div>
                    <div className={classes.detailContent}>
                      {generatedToken ? (
                        <>
                          <div className={classes.tokenValue}>
                            <span
                              className={classes.tokenText}
                              title={generatedToken}
                            >
                              {getTruncatedToken(generatedToken)}
                            </span>
                            <Tooltip title={t("copy") as string}>
                              <IconButton
                                aria-label={t("copy")}
                                className={classes.copyButton}
                                onClick={handleCopyToken}
                              >
                                <FileCopyOutlinedIcon
                                  className={classes.copyIcon}
                                />
                              </IconButton>
                            </Tooltip>
                          </div>
                          <div className={classes.tokenWarning}>
                            {t("scim_token_only_available_now")}
                          </div>
                        </>
                      ) : (
                        <Button
                          className={classes.linkButton}
                          classes={{ label: classes.linkButtonLabel }}
                          disabled={generateTokenPending}
                          onClick={handleGenerateToken}
                        >
                          {generateTokenPending ? (
                            <CircularProgress size={14} />
                          ) : (
                            t("generate_new_token")
                          )}
                        </Button>
                      )}
                      {generateTokenError && (
                        <div className={classes.tokenError}>
                          {generateTokenError}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={classes.detailsDeleteAction}>
                    <Button
                      className={classes.deleteButton}
                      classes={{ label: classes.deleteButtonLabel }}
                      onClick={() => setConfirmDelete(true)}
                      variant="contained"
                    >
                      {t("delete_configuration")}
                    </Button>
                  </div>
                  {deleteConfigurationError && (
                    <Typography align="center" color="error">
                      {deleteConfigurationError}
                    </Typography>
                  )}
                </>
              )}
            </div>
            {!showDetails && (
              <div className={classes.actions}>
                <Button
                  className={classes.createButton}
                  classes={{ label: classes.createButtonLabel }}
                  color="primary"
                  onClick={() => setConfirmCreate(true)}
                  variant="contained"
                >
                  {t("create_new_configuration")}
                </Button>
              </div>
            )}
            {!showDetails && deleteConfigurationError && (
              <Typography align="center" color="error">
                {deleteConfigurationError}
              </Typography>
            )}
          </>
        )}
      </Paper>
      <ConfirmDialog
        dialogType={DialogTypes.warning}
        toggle={confirmCreate}
        title={t("create_new_configuration")}
        message={t("confirm_create_new_entra_configuration")}
        possitiveText={t("continue")}
        negativeText={t("cancel")}
        positiveAction={handleCreateNewConfiguration}
        negativeAction={() => void 0}
        onClose={() => setConfirmCreate(false)}
      />
      <ConfirmDialog
        dialogType={DialogTypes.warning}
        toggle={confirmDelete}
        title={t("delete_configuration")}
        message={t("confirm_delete_sso_configuration")}
        possitiveText={t("delete")}
        negativeText={t("cancel")}
        positiveAction={handleDeleteConfiguration}
        negativeAction={() => void 0}
        onClose={() => setConfirmDelete(false)}
      />
      <Dialog
        open={showWizard}
        aria-labelledby="entra-configuration-setup-title"
        maxWidth={false}
        classes={{ paper: classes.wizardPaper }}
      >
        <DialogContent className={classes.wizardContent}>
          <Tooltip title={t("close") as string}>
            <IconButton
              aria-label={t("close")}
              className={classes.wizardCloseButton}
              onClick={handleCloseWizard}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
          <h2
            className={classes.wizardTitle}
            id="entra-configuration-setup-title"
          >
            {t("entra_configuration_setup")}
          </h2>
          {wizardStep === 1 ? (
            <>
              <div className={classes.wizardStepTitle}>
                {t("section_1_of_3")}
              </div>
              <div className={classes.wizardSectionTitle}>
                {setupSubmitted
                  ? t("provisioning")
                  : t("entra_setup_section_1_title")}
              </div>

              <div className={classes.wizardRow}>
                <Checkbox
                  checked={sectionOneChecks.createApp}
                  className={classes.wizardCheckbox}
                  onChange={() => handleSectionOneCheck("createApp")}
                />
                <div className={classes.wizardInstruction}>
                  {t("entra_setup_create_app_prefix")}{" "}
                  <Link
                    className={classes.wizardLink}
                    href={ENTRA_SETUP_GUIDE_URL}
                    rel="noopener noreferrer"
                    target="_blank"
                    underline="none"
                  >
                    {t("stackcare_entra_setup_guide")}
                  </Link>{" "}
                  {t("entra_setup_create_app_suffix")}
                </div>
              </div>

              <div className={classes.wizardRow}>
                <Checkbox
                  checked={sectionOneChecks.completeSAML}
                  className={classes.wizardCheckbox}
                  onChange={() => handleSectionOneCheck("completeSAML")}
                />
                <div className={classes.wizardInstruction}>
                  {t("entra_setup_complete_saml")}
                </div>
              </div>

              <div className={classes.wizardRow}>
                <Checkbox
                  checked={setupSubmitted}
                  className={classes.wizardCheckbox}
                  disabled
                />
                <div className={classes.wizardInstruction}>
                  {t("entra_setup_submit_identifier_metadata")}
                </div>
              </div>

              <div className={classes.wizardForm}>
                <div className={classes.wizardFormRow}>
                  <div className={classes.wizardFormLabel}>
                    {t("identifier_entity_id")}
                  </div>
                  <TextField
                    className={classes.wizardInput}
                    disabled={setupSubmitted}
                    onChange={(event) => setEntityID(event.target.value)}
                    placeholder={t("identifier_entity_id_placeholder")}
                    value={entityID}
                    variant="outlined"
                  />
                </div>
                <div className={classes.wizardFormRow}>
                  <div className={classes.wizardFormLabel}>
                    {t("metadata_url")}
                  </div>
                  <TextField
                    className={classes.wizardInput}
                    disabled={setupSubmitted}
                    onChange={(event) => setMetadataURL(event.target.value)}
                    placeholder={t("metadata_url_placeholder")}
                    value={metadataURL}
                    variant="outlined"
                  />
                </div>
                <div className={classes.wizardSubmitRow}>
                  <Button
                    className={classes.wizardActionButton}
                    color="primary"
                    disabled={!canSubmitSetup}
                    onClick={handleSubmitSetup}
                    variant="outlined"
                  >
                    {setupPending ? (
                      <CircularProgress size={16} />
                    ) : (
                      t("submit")
                    )}
                  </Button>
                </div>
                {setupSubmitted && (
                  <div className={classes.wizardSuccess}>
                    {t("submitted_successfully")}
                  </div>
                )}
                {setupError && (
                  <div className={classes.wizardError}>{setupError}</div>
                )}
              </div>

              <div
                className={`${classes.wizardRow} ${classes.wizardBottomStep}`}
              >
                <Checkbox
                  checked={sectionOneChecks.validateSSO}
                  className={classes.wizardCheckbox}
                  onChange={() => handleSectionOneCheck("validateSSO")}
                />
                <div className={classes.wizardInstruction}>
                  {t("entra_setup_validate_sso")}
                </div>
              </div>
              <div className={classes.wizardNextRow}>
                <Button
                  className={classes.wizardActionButton}
                  disabled={!canContinueToStepTwo}
                  onClick={() => setWizardStep(2)}
                  variant="outlined"
                >
                  {t("next")}
                </Button>
              </div>
            </>
          ) : wizardStep === 2 ? (
            <>
              <div className={classes.wizardStepTitle}>
                {t("section_2_of_3")}
              </div>
              <div className={classes.wizardSectionTitle}>
                {t("enterprise_app_provisioning")}
              </div>
              <div className={classes.wizardRow}>
                <Checkbox
                  checked={sectionTwoChecks.guide}
                  className={classes.wizardCheckbox}
                  onChange={() => handleSectionTwoCheck("guide")}
                />
                <div className={classes.wizardInstruction}>
                  {t("entra_setup_provisioning_guide_prefix")}{" "}
                  <strong>{t("enterprise_app_provisioning")}</strong>{" "}
                  {t("entra_setup_provisioning_guide_middle")}{" "}
                  <Link
                    className={classes.wizardLink}
                    href={ENTRA_SETUP_GUIDE_URL}
                    rel="noopener noreferrer"
                    target="_blank"
                    underline="none"
                  >
                    {t("stackcare_entra_setup_guide")}
                  </Link>{" "}
                  {t("entra_setup_provisioning_guide_suffix")}
                </div>
              </div>
              <div className={classes.wizardRow}>
                <Checkbox
                  checked={sectionTwoChecks.credentials}
                  className={classes.wizardCheckbox}
                  onChange={() => handleSectionTwoCheck("credentials")}
                />
                <div className={classes.wizardInstruction}>
                  {t("entra_setup_enter_tenant_token")}
                </div>
              </div>
              <div className={classes.wizardForm}>
                <div className={classes.wizardFormRow}>
                  <div className={classes.wizardFormLabel}>
                    {t("tenant_url")}
                  </div>
                  <TextField
                    className={classes.wizardReadonlyField}
                    InputProps={{ readOnly: true }}
                    value={wizardTenantURL || tenantURL}
                    variant="outlined"
                  />
                </div>
                <div className={classes.wizardFormRow}>
                  <div className={classes.wizardFormLabel}>
                    {t("secret_token")}
                  </div>
                  <div className={classes.wizardTokenField}>
                    <span
                      className={classes.wizardTokenText}
                      title={generatedToken}
                    >
                      {getTruncatedToken(generatedToken)}
                    </span>
                    <Tooltip title={t("copy") as string}>
                      <IconButton
                        aria-label={t("copy")}
                        className={classes.copyButton}
                        onClick={handleCopyToken}
                      >
                        <FileCopyOutlinedIcon className={classes.copyIcon} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div
                className={`${classes.wizardRow} ${classes.wizardBottomStep}`}
              >
                <Checkbox
                  checked={sectionTwoChecks.testConnection}
                  className={classes.wizardCheckbox}
                  onChange={() => handleSectionTwoCheck("testConnection")}
                />
                <div className={classes.wizardInstruction}>
                  {t("test_provisioning_connection")}
                </div>
              </div>
              <div className={classes.wizardRow}>
                <Checkbox
                  checked={sectionTwoChecks.createProvisioning}
                  className={classes.wizardCheckbox}
                  onChange={() => handleSectionTwoCheck("createProvisioning")}
                />
                <div className={classes.wizardInstruction}>
                  {t("create_provisioning_configuration")}
                </div>
              </div>
              <div className={classes.wizardRow}>
                <Checkbox
                  checked={sectionTwoChecks.startProvisioning}
                  className={classes.wizardCheckbox}
                  onChange={() => handleSectionTwoCheck("startProvisioning")}
                />
                <div className={classes.wizardInstruction}>
                  {t("start_provisioning_configuration")}
                </div>
              </div>
              <div className={classes.wizardNextRow}>
                <Button
                  className={classes.wizardPrimaryButton}
                  disabled={!canContinuePastStepTwo}
                  onClick={() => setWizardStep(3)}
                  variant="contained"
                >
                  {t("next")}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className={classes.wizardStepTitle}>
                {t("section_3_of_3")}
              </div>
              <div className={classes.wizardSectionTitle}>
                {t("stackcare_logo")}
              </div>
              <div className={classes.wizardRow}>
                <Checkbox
                  checked={sectionThreeChecks.logo}
                  className={classes.wizardCheckbox}
                  onChange={() => handleSectionThreeCheck("logo")}
                />
                <div className={classes.wizardInstruction}>
                  {t("entra_setup_logo_prefix")}{" "}
                  <strong>{t("stackcare_logo")}</strong>{" "}
                  {t("entra_setup_logo_middle")}{" "}
                  <Link
                    className={classes.wizardLink}
                    href={ENTRA_SETUP_GUIDE_URL}
                    rel="noopener noreferrer"
                    target="_blank"
                    underline="none"
                  >
                    {t("stackcare_entra_setup_guide")}
                  </Link>{" "}
                  {t("entra_setup_logo_suffix")}
                </div>
              </div>
              {canFinishWizard && (
                <div className={classes.wizardCompletionMessage}>
                  <div className={classes.wizardCompletionParagraph}>
                    {t("entra_configuration_complete")}
                  </div>
                  <div className={classes.wizardCompletionParagraph}>
                    {t("entra_configuration_complete_group_mapping")}
                  </div>
                  <div>
                    {t("entra_configuration_complete_guide_prefix")}{" "}
                    <Link
                      className={classes.wizardLink}
                      href={ENTRA_SETUP_GUIDE_URL}
                      rel="noopener noreferrer"
                      target="_blank"
                      underline="none"
                    >
                      {t("stackcare_entra_setup_guide")}
                    </Link>{" "}
                    {t("entra_configuration_complete_guide_suffix")}
                  </div>
                </div>
              )}
              <div className={classes.wizardNextRow}>
                <Button
                  className={classes.wizardPrimaryButton}
                  disabled={!canFinishWizard}
                  onClick={handleFinishWizard}
                  variant="contained"
                >
                  {t("done")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
